import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const AREA_GUIDE_LIMIT = 4;
const FALLBACK_IMAGE = "assets/img/property-values/property-values-2-1.jpg";

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return `${baseUrl}${pathname}`;
}

function imageWithVariant(image, variant, fallback) {
  return image ? `${image}/${variant}` : fallback;
}

function areaTitle(area) {
  return area.title || area.Location || "Area Guide";
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function areaUrl(area) {
  const location = areaTitle(area);
  const slug = slugify(location) || "area";

  return `/offplan-by-area/${slug}`;
}

function propertyCount(area) {
  const count = area.total_property || area.total_views;
  return count ? `${count} Properties` : "Explore Properties";
}

async function fetchAreaGuides(signal) {
  const primaryRequest = {
    pathname: "/api/areaguideapi.ashx",
    options: {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        typ: "Area Guide",
        home: "Yes",
        id: "0",
      }),
    },
  };

  const fallbackRequest = {
    pathname: "/api/topareainabudhabi.ashx",
    options: {
      method: "GET",
    },
  };

  let lastError = null;

  for (const request of [primaryRequest, fallbackRequest]) {
    try {
      const response = await fetch(buildApiUrl(request.pathname), {
        ...request.options,
        signal,
      });

      if (!response.ok) {
        throw new Error(`Unable to load area guide from ${request.pathname}`);
      }

      const payload = await response.json();

      if (payload.result === 1 && Array.isArray(payload.data)) {
        return payload.data.slice(0, AREA_GUIDE_LIMIT);
      }

      throw new Error(payload.message || `Unable to load area guide from ${request.pathname}`);
    } catch (err) {
      if (err.name === "AbortError") {
        throw err;
      }

      lastError = err;
    }
  }

  throw lastError || new Error("Unable to load area guide");
}

function AreaGuideCard({ area, size }) {
  const location = areaTitle(area);
  const linkState = { location };

  return (
    <div className={`thumb area-guide-card area-guide-card--${size}`}>
      <img src={imageWithVariant(area.Image || area.ImagePath, "w=700,sharpen=1", FALLBACK_IMAGE)} alt={location} />
      <Link className="plus-icon" to={areaUrl(area)} state={linkState} aria-label={`View ${location} off-plan projects`}>
        <i className="fa-solid fa-plus" />
      </Link>
      <div className="hover-content">
        <h4 className="box-title">
          <Link to={areaUrl(area)} state={linkState}>
            {location}
          </Link>
        </h4>
        <p className="box-text">{propertyCount(area)}</p>
        <Link className="th-btn bg-theme pill" to={areaUrl(area)} state={linkState}>
          View Area
        </Link>
      </div>
    </div>
  );
}

function AreaGuide() {
  const [areas, setAreas] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    async function loadAreas() {
      setStatus("loading");

      try {
        const data = await fetchAreaGuides(controller.signal);
        setAreas(data);
        setStatus("ready");
      } catch (err) {
        if (err.name !== "AbortError") {
          setAreas([]);
          setStatus("error");
        }
      }
    }

    loadAreas();

    return () => controller.abort();
  }, []);

  const columns = useMemo(() => {
    return [areas.slice(0, 1), areas.slice(1, 3), areas.slice(3, 4)].filter((column) => column.length);
  }, [areas]);

  return (
    <section className="property-values-sec-2 area-guide-section space overflow-hidden">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-7">
            <div className="title-area text-center">
              <h2 className="sec-title fadeinup wow" data-wow-duration="1.5s" data-wow-delay="0.3s">
                Area Guide
              </h2>
            </div>
          </div>
        </div>
        {status === "loading" && <div className="text-center">Loading area guide...</div>}
        {status === "error" && <div className="text-center">Unable to load area guide.</div>}
        {status === "ready" && areas.length === 0 && <div className="text-center">No area guide found.</div>}
        {status === "ready" && areas.length > 0 && (
          <div className="area-guide-grid">
            {columns.map((column, columnIndex) => (
              <div className="property-values-2-item area-guide-column" key={columnIndex}>
                {column.map((area, itemIndex) => (
                  <AreaGuideCard area={area} key={area.id || areaTitle(area)} size={columnIndex === 1 ? "short" : "tall"} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        .area-guide-section .title-area {
          margin-bottom: 28px;
        }

        .area-guide-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 28px;
          align-items: stretch;
        }

        .area-guide-section .area-guide-column {
          height: 100%;
          gap: 28px;
        }

        .area-guide-section .area-guide-card {
          width: 100%;
          background: #f4f4f4;
        }

        .area-guide-section .area-guide-card--tall {
          height: 576px;
        }

        .area-guide-section .area-guide-card--short {
          height: 274px;
        }

        .area-guide-section .property-values-2-item .thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .area-guide-section .property-values-2-item .thumb .plus-icon {
          left: 48px;
          bottom: 48px;
          height: 58px;
          width: 58px;
          line-height: 58px;
          font-size: 22px;
        }

        @media (max-width: 1399px) {
          .area-guide-section .area-guide-card--tall {
            height: 520px;
          }

          .area-guide-section .area-guide-card--short {
            height: 246px;
          }
        }

        @media (max-width: 1199px) {
          .area-guide-grid {
            gap: 24px;
          }

          .area-guide-section .area-guide-column {
            gap: 24px;
          }

          .area-guide-section .area-guide-card--tall {
            height: 460px;
          }

          .area-guide-section .area-guide-card--short {
            height: 218px;
          }
        }

        @media (max-width: 991px) {
          .area-guide-grid {
            grid-template-columns: 1fr;
          }

          .area-guide-section .area-guide-card--tall,
          .area-guide-section .area-guide-card--short {
            height: 360px;
          }
        }

        @media (max-width: 575px) {
          .area-guide-section .title-area {
            margin-bottom: 20px;
          }

          .area-guide-section .area-guide-card--tall,
          .area-guide-section .area-guide-card--short {
            height: 280px;
          }

          .area-guide-section .property-values-2-item .thumb .plus-icon {
            left: 24px;
            bottom: 24px;
            height: 48px;
            width: 48px;
            line-height: 48px;
            font-size: 18px;
          }
        }
      `}</style>
    </section>
  );
}

export default AreaGuide;
